import { Suspense, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuth } from '@/hooks/useAuth';
import { UserMenu } from '@/components/auth/UserMenu';
import { SyncStatus } from '@/components/sync/SyncStatus';
import { useLibraryStore } from '@/stores/library.store';
import { supabase } from '@/lib/supabase';

// Loading fallback component with accessibility support
function PageLoader() {
  return (
    <div
      className="flex min-h-[50vh] items-center justify-center"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"
          aria-hidden="true"
        />
        <p className="text-sm text-slate-500">Loading...</p>
        <span className="sr-only">Loading page content, please wait</span>
      </div>
    </div>
  );
}

// Map paths to page titles for accessibility
function getPageTitle(pathname: string): string {
  const titles: Record<string, string> = {
    '/': 'Home',
    '/analysis': 'Property Analysis',
    '/library': 'Property Library',
    '/blog': 'Blog',
    '/about': 'About Us',
    '/pricing': 'Pricing',
    '/login': 'Sign In',
    '/signup': 'Create Account',
    '/forgot-password': 'Forgot Password',
    '/reset-password': 'Reset Password',
    '/dashboard': 'Dashboard',
    '/dashboard/account': 'Account Settings',
    '/dashboard/subscription': 'Subscription',
    '/dashboard/billing': 'Billing History',
  };
  return titles[pathname] || 'Page';
}

export function RootLayout() {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const pageTitle = getPageTitle(location.pathname);
  const setAuthenticated = useLibraryStore((state) => state.setAuthenticated);

  // Connect auth state to library store for cloud sync
  useEffect(() => {
    if (user && supabase) {
      // User logged in - enable cloud sync
      setAuthenticated(user.id, supabase);
    } else if (!isLoading) {
      // User logged out - switch to local-only mode
      setAuthenticated(null, null);
    }
  }, [user, isLoading, setAuthenticated]);

  const navLinkClass = (path: string) => {
    const isActive = location.pathname === path ||
      (path !== '/' && location.pathname.startsWith(path));
    return isActive
      ? 'text-sm font-semibold text-white'
      : 'text-sm font-medium text-white/80 hover:text-white';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Unified Header - consistent across all pages */}
      <header role="banner" className="sticky top-0 z-50 bg-gradient-to-r from-teal-500 to-cyan-500 shadow-md">
        <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Real(Estate) Analytics" className="h-[60px] w-auto" style={{ filter: 'brightness(0) invert(1)' }} />
            </Link>
            <nav role="navigation" aria-label="Main navigation" className="flex items-center gap-6">
              {user && (
                <>
                  <Link
                    to="/analysis"
                    className={navLinkClass('/analysis')}
                    aria-current={location.pathname === '/analysis' ? 'page' : undefined}
                  >
                    Analysis
                  </Link>
                  <Link
                    to="/library"
                    className={navLinkClass('/library')}
                    aria-current={location.pathname === '/library' ? 'page' : undefined}
                  >
                    Library
                  </Link>
                </>
              )}
              <Link
                to="/blog"
                className={navLinkClass('/blog')}
                aria-current={location.pathname === '/blog' ? 'page' : undefined}
              >
                Blog
              </Link>
              <Link
                to="/about"
                className={navLinkClass('/about')}
                aria-current={location.pathname === '/about' ? 'page' : undefined}
              >
                About Us
              </Link>

              {/* Auth section */}
              <div className="ml-4 flex items-center gap-3 border-l border-white/30 pl-4">
                {isLoading ? (
                  <div className="h-8 w-8 animate-pulse rounded-full bg-white/20" />
                ) : user ? (
                  <>
                    <SyncStatus showLabel={false} />
                    <UserMenu />
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-sm font-medium text-white/80 hover:text-white"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/signup"
                      className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-teal-600 hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-teal-500"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      <main id="main-content" role="main" aria-label={pageTitle}>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}
